import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

/* ─────────────────────────────────────────────────────────────
   Hooks
───────────────────────────────────────────────────────────── */

/**
 * Locks background scrolling while a sheet/modal is open.
 * On iOS a plain `overflow:hidden` still lets the page scroll, so we pin the
 * body and restore the exact scroll position on close.
 */
export function useBodyScrollLock(active = true) {
  useEffect(() => {
    if (!active) return;
    const y = window.scrollY;
    const { body } = document;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${y}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, y);
    };
  }, [active]);
}

/** Closes on Escape. */
export function useEscape(onEscape, active = true) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === "Escape" && onEscape?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onEscape, active]);
}

/* ─────────────────────────────────────────────────────────────
   Primitives
───────────────────────────────────────────────────────────── */

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="font-display text-[1.65rem] sm:text-3xl font-bold tracking-tight text-slate-900 truncate">
          {title}
        </h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Card({ as: Tag = "div", className = "", children, ...rest }) {
  return (
    <Tag
      className={`rounded-2xl border border-slate-200 bg-white shadow-card ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {children}
      </h2>
      {action}
    </div>
  );
}

const BUTTON_VARIANTS = {
  // brand-700 is the lightest green that clears 4.5:1 against white text.
  primary:
    "bg-brand-700 text-white shadow-sm hover:bg-brand-800 active:bg-brand-800 disabled:bg-brand-700/50",
  danger:
    "bg-red-600 text-white shadow-sm hover:bg-red-700 active:bg-red-700 disabled:bg-red-600/50",
  secondary:
    "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 active:bg-slate-100 disabled:text-slate-400",
  subtle:
    "bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-200 disabled:text-slate-400",
  ghost:
    "text-slate-600 hover:bg-slate-100 active:bg-slate-200 disabled:text-slate-400",
};

const BUTTON_SIZES = {
  // Nothing drops below 44px — this app is used one-handed, outdoors, in gloves.
  sm: "h-11 px-3.5 text-sm gap-1.5",
  md: "h-11 px-4 text-sm gap-2",
  lg: "h-14 px-6 text-base gap-2.5",
  xl: "h-16 px-6 text-lg gap-3",
};

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  block = false,
  className = "",
  children,
  disabled,
  ...rest
}) {
  return (
    <button
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        "inline-flex items-center justify-center rounded-xl font-display font-semibold",
        "transition-colors duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100",
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        block ? "w-full" : "",
        className,
      ].join(" ")}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner({ className = "h-4 w-4" }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-block shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70 ${className}`}
    />
  );
}

const BADGE_TONES = {
  green: "bg-brand-50 text-brand-800 border-brand-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  red:   "bg-red-50 text-red-700 border-red-200",
  blue:  "bg-blue-50 text-blue-700 border-blue-200",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
};

export function Badge({ tone = "slate", className = "", children }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${BADGE_TONES[tone] || BADGE_TONES.slate} ${className}`}
    >
      {children}
    </span>
  );
}

const TILE_TONES = {
  green: { wrap: "border-brand-200 bg-brand-50", val: "text-brand-800", chip: "bg-brand-100 text-brand-700" },
  amber: { wrap: "border-amber-200 bg-amber-50", val: "text-amber-800", chip: "bg-amber-100 text-amber-700" },
  blue:  { wrap: "border-blue-200 bg-blue-50",   val: "text-blue-800",  chip: "bg-blue-100 text-blue-700" },
  slate: { wrap: "border-slate-200 bg-white",    val: "text-slate-800", chip: "bg-slate-100 text-slate-600" },
};

/** `icon` takes an icon *component* so tiles match the rest of the UI's
 *  stroke icons instead of mixing in platform emoji. */
export function StatTile({ icon: Icon, label, value, tone = "slate", to, loading }) {
  const t = TILE_TONES[tone] || TILE_TONES.slate;
  const body = (
    <>
      {Icon && (
        <span
          aria-hidden="true"
          className={`mb-2.5 flex h-9 w-9 items-center justify-center rounded-xl ${t.chip}`}
        >
          <Icon className="h-5 w-5" />
        </span>
      )}
      <div className={`font-display text-2xl font-bold leading-none tabular ${t.val}`}>
        {loading ? <span className="text-slate-300">—</span> : value}
      </div>
      <div className="mt-1 text-xs font-medium leading-snug text-slate-600">{label}</div>
    </>
  );

  const cls = `block rounded-2xl border p-3.5 text-left shadow-card sm:p-4 ${t.wrap}`;

  return to ? (
    <Link
      to={to}
      className={`${cls} transition-transform active:scale-[0.97] hover:shadow-lift`}
    >
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

export function EmptyState({ icon = "📭", title, hint, action }) {
  return (
    <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-12 text-center">
      <div className="text-4xl" aria-hidden="true">{icon}</div>
      <p className="mt-3 font-display font-semibold text-slate-700">{title}</p>
      {hint && <p className="mt-1 max-w-xs text-sm text-slate-500">{hint}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({ message = "Something went wrong.", onRetry }) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center rounded-2xl border border-red-200 bg-red-50 px-6 py-10 text-center"
    >
      <div className="text-3xl" aria-hidden="true">⚠️</div>
      <p className="mt-3 font-display font-semibold text-red-800">{message}</p>
      <p className="mt-1 text-sm text-red-700/80">Check your connection and try again.</p>
      {onRetry && (
        <Button variant="secondary" size="md" className="mt-5" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

export function Skeleton({ className = "h-20" }) {
  return <div className={`skeleton ${className}`} aria-hidden="true" />;
}

export function SkeletonList({ count = 3, className = "h-20 rounded-2xl" }) {
  return (
    <div className="space-y-3" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className={className} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Form controls
───────────────────────────────────────────────────────────── */

const CONTROL_BASE =
  "w-full rounded-xl border border-slate-300 bg-white px-4 text-slate-900 placeholder-slate-400 " +
  "transition-shadow focus:border-brand-600 focus:outline-none focus:ring-4 focus:ring-brand-600/15 " +
  "disabled:bg-slate-100 disabled:text-slate-500";

export function Field({ label, hint, required, htmlFor, children, className = "" }) {
  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600"
        >
          {label}
          {required && <span className="ml-0.5 text-red-600" aria-hidden="true">*</span>}
        </label>
      )}
      {children}
      {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export function Input({ className = "", ...rest }) {
  return <input className={`${CONTROL_BASE} h-12 md:h-11 md:text-sm ${className}`} {...rest} />;
}

export function Select({ className = "", children, ...rest }) {
  return (
    <div className="relative">
      <select
        className={`${CONTROL_BASE} h-12 appearance-none pr-10 md:h-11 md:text-sm ${className}`}
        {...rest}
      >
        {children}
      </select>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-500"
      >
        ▾
      </span>
    </div>
  );
}

export function Textarea({ className = "", rows = 3, ...rest }) {
  return (
    <textarea
      rows={rows}
      className={`${CONTROL_BASE} py-3 leading-relaxed md:text-sm ${className}`}
      {...rest}
    />
  );
}

/** Scrollable pill filter row — never wraps or overflows on narrow screens. */
export function ChipRow({ children, className = "" }) {
  return (
    <div
      className={`no-scrollbar snap-row edge-bleed flex gap-2 overflow-x-auto py-1 ${className}`}
    >
      {children}
    </div>
  );
}

export function Chip({ active, className = "", children, ...rest }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={[
        "inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-4 text-sm font-medium",
        "transition-colors active:scale-[0.97]",
        active
          ? "border-brand-700 bg-brand-700 text-white"
          : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50",
        className,
      ].join(" ")}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────
   Sheet — bottom sheet on phones, centred dialog from sm up
───────────────────────────────────────────────────────────── */

export function Sheet({ title, subtitle, onClose, children, footer, labelledBy = "sheet-title" }) {
  const panelRef = useRef(null);
  useBodyScrollLock(true);
  useEscape(onClose);

  // Move focus into the dialog so keyboard/screen-reader users land inside it.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
      <div
        className="fade-in absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className="slide-up relative flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-lift outline-none sm:max-w-lg sm:rounded-3xl"
      >
        {/* Drag affordance — signals "swipeable sheet" on mobile only */}
        <div className="pt-2.5 sm:hidden" aria-hidden="true">
          <div className="mx-auto h-1.5 w-10 rounded-full bg-slate-300" />
        </div>

        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 id={labelledBy} className="font-display font-semibold text-slate-900">
              {title}
            </h2>
            {subtitle && <p className="truncate text-xs text-slate-500">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="tap -mr-2 -mt-2 flex items-center justify-center rounded-xl text-2xl leading-none text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5">{children}</div>

        {footer && (
          <div className="border-t border-slate-200 bg-white px-5 py-4 pb-safe">{footer}</div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Formatting helpers
───────────────────────────────────────────────────────────── */

const inr = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

/** ₹1,240 · ₹1.2L · ₹12.4K — readable at any magnitude, unlike a fixed "K". */
export function formatMoney(n = 0) {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${inr.format(v)}`;
}

export function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

export function formatDate(value, opts = { day: "numeric", month: "short", year: "numeric" }) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", opts);
}
